import React from 'react';
import { versDate } from '../services/horodatage';
import { dateFr as dateDuJour } from '../services/dates';

/**
 * SUIVI DE STAGE — LA FICHE DE LIAISON ENTIÈRE (retour de production du 22/10, maquette rév. 11)
 * ==============================================================================================
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html — `#ficheCol3` (colonne du volet, dossier FAURE)
 * et le dépliable sous la carte « Fiche de liaison » de l'écran Dossier. Adresse de démonstration : #fiche-liaison.
 *
 * ⚠️ POURQUOI CE COMPOSANT EXISTE : les réponses aux questions du modèle de fiche (sections optionnelles) n'étaient
 * affichées NULLE PART — le serveur envoyait { id: valeur } sans titre et aucun écran ne le lisait. Vu en production
 * sur le dossier BALASSOUPRAMANIEN SAGANA. Le serveur rend désormais `fiche.formulaire` (stageFicheLiaisonService
 * .ficheComplete) : les questions dans l'ordre du formulaire de la campagne, avec leur titre et qui les remplit.
 *
 * Un seul contenu pour les deux écrans : la colonne du volet et le dépliable du Dossier ne doivent jamais dire deux
 * choses différentes de la même fiche.
 *
 * Décisions de Bertrand (22/10) : la fiche ENTIÈRE (pas seulement les questions), « non renseigné » visible (sinon on
 * ne sait pas si la question a été posée), ✏️ + ancienne valeur barrée pour ce que le maître a changé (E173), qui
 * remplit chaque champ.
 */

const QUI = { etudiant: 'Étudiant', maitre: 'Maître', les_deux: 'Les deux' };
const CODE_CONVENTION = 'convention_universite';

// ⚠️ `versDate` : une heure sans fuseau venant de la base est de l'UTC (écart E78).
const jourDe = (iso) => {
    const d = versDate(iso);
    return d ? d.toLocaleDateString('fr-FR') : null;
};

const vide = (v) => v === null || v === undefined || String(v).trim() === '';

/** Une réponse telle qu'on la lit : une date d'un champ « date » au format jj/mm/aaaa, le reste tel quel. */
function texteReponse(q) {
    if (vide(q.valeur)) return null;
    return q.type_champ === 'date' ? dateDuJour(q.valeur) : String(q.valeur);
}

function ChampFiche({ question, qui, maitre = false, valeur, avant }) {
    const modifie = avant !== undefined;
    return (
        <div className="fl-champ">
            <div className="fl-q">
                <span>{question}</span>
                {modifie && <span className="fl-modif"><i className="fa-solid fa-pen" /> modifiée</span>}
                <span className={'fl-qui' + (maitre ? ' maitre' : '')}>{modifie ? `${qui} · corrigé par le maître` : qui}</span>
            </div>
            {modifie && <div className="fl-avant">{vide(avant) ? '(vide)' : avant}</div>}
            {vide(valeur)
                ? <div className="fl-r vide">non renseigné</div>
                : <div className="fl-r">{valeur}</div>}
        </div>
    );
}

/**
 * `fiche` = `dossier.fiche` du serveur (l'état de la fiche se dit à part : `LigneEtatFiche`).
 * `sansCoordonnees` : l'écran Dossier affiche déjà l'entreprise et le maître au-dessus du dépliable (maquette rév. 11).
 */
export default function StageFicheLiaisonContenu({ fiche, sansCoordonnees = false }) {
    if (!fiche) return null;
    const f = fiche.formulaire || { questions: [] };

    // E173 : ce que le maître a changé — { champ: avant }. Les sections ont pour champ « section_<id> ».
    const avantDe = Object.fromEntries((fiche.modifications_maitre || []).map((m) => [m.champ, m.avant]));
    const avantGroupe = (champs, recomposer) => (champs.some((c) => c in avantDe)
        ? recomposer(Object.fromEntries(champs.map((c) => [c, c in avantDe ? avantDe[c] : fiche[c]])))
        : undefined);

    const adresse = (x) => [x.entreprise_adresse, [x.entreprise_cp, x.entreprise_ville].filter(Boolean).join(' ')]
        .filter((t) => !vide(t)).join(', ');
    const nomMaitre = (x) => [fiche.maitre_civilite, (x.maitre_nom || '').toUpperCase(), x.maitre_prenom]
        .filter((t) => !vide(t)).join(' ');

    const questions = f.questions.filter((q) => q.code !== CODE_CONVENTION);
    const convention = f.questions.find((q) => q.code === CODE_CONVENTION);
    const missionsPresentes = f.missions_affichees || !vide(fiche.missions);

    return (
        <>
            {!sansCoordonnees && <>
            <div className="fl-bloc">
                <div className="fl-bloc-titre">Entreprise</div>
                <ChampFiche question="Raison sociale" qui="Étudiant" valeur={fiche.entreprise_nom} avant={avantDe.entreprise_nom} />
                <ChampFiche question="Adresse" qui="Étudiant" valeur={adresse(fiche)}
                            avant={avantGroupe(['entreprise_adresse', 'entreprise_cp', 'entreprise_ville'], adresse)} />
            </div>

            <div className="fl-bloc">
                <div className="fl-bloc-titre">Maître de stage</div>
                <ChampFiche question="Nom" qui="Étudiant" valeur={nomMaitre(fiche)}
                            avant={avantGroupe(['maitre_nom', 'maitre_prenom'], nomMaitre)} />
                <ChampFiche question="Fonction" qui="Étudiant" valeur={fiche.maitre_fonction} avant={avantDe.maitre_fonction} />
                <ChampFiche question="Courriel" qui="Étudiant" valeur={fiche.maitre_email} avant={avantDe.maitre_email} />
                <ChampFiche question="Téléphone" qui="Étudiant" valeur={fiche.maitre_telephone} avant={avantDe.maitre_telephone} />
            </div>
            </>}

            {missionsPresentes && (
                <div className="fl-bloc">
                    <div className="fl-bloc-titre">Missions proposées</div>
                    <ChampFiche question={f.missions_libelle || 'Missions'} qui="Étudiant"
                                valeur={fiche.missions} avant={avantDe.missions} />
                </div>
            )}

            {questions.length > 0 && (
                <div className="fl-bloc">
                    <div className="fl-bloc-titre">Questions du modèle de fiche</div>
                    {questions.map((q) => (
                        <ChampFiche key={q.id} question={q.titre} qui={QUI[q.remplissage] || q.remplissage}
                                    maitre={q.remplissage === 'maitre'} valeur={texteReponse(q)}
                                    avant={avantDe[`section_${q.id}`]} />
                    ))}
                </div>
            )}

            {convention && (
                <div className="fl-bloc">
                    <div className="fl-bloc-titre">Convention</div>
                    {/* La case se lit comme le tableau de bord : trois états — oui, non, pas encore répondu (lot 3.2 bis). */}
                    <ChampFiche question={<><i className="fa-solid fa-building-columns" style={{ color: '#64748b' }} /> {convention.titre}</>}
                                qui="Maître" maitre
                                valeur={fiche.convention_universite === true ? 'Oui' : fiche.convention_universite === false ? 'Non' : null} />
                </div>
            )}
        </>
    );
}

/** La ligne d'état de la fiche : saisie, validation du maître, modifications, formulaire figé. */
export function LigneEtatFiche({ fiche }) {
    if (!fiche) return null;
    const n = (fiche.modifications_maitre || []).length;
    const fige = fiche.formulaire?.fige_le ? jourDe(fiche.formulaire.fige_le) : null;
    const saisie = jourDe(fiche.soumise_etudiant_le);
    const validee = jourDe(fiche.soumise_maitre_le);
    return (
        <div className="fl-tete-l2">
            Version actuelle{saisie ? ` : saisie par l'étudiant le ${saisie}` : ''}
            {validee ? `, validée par le maître le ${validee}` : ' — le maître ne l\'a pas encore validée'}
            {n > 0 && <> · <span className="fl-modif"><i className="fa-solid fa-pen" /> {n} information{n > 1 ? 's' : ''} modifiée{n > 1 ? 's' : ''} par le maître</span></>}
            <br />
            {fige ? <>Formulaire de la campagne (figé au 1<sup>er</sup> envoi, le {fige}).</> : 'Formulaire du processus (pas encore figé).'}
        </div>
    );
}
